import { fetchQueue } from 'flow/actions/pbx-actions';
import { fetchDefaultSounds } from 'flow/actions/sounds-actions';
import SettingsListItem from 'flow/components/custom-components/SettingsListItem';
import Page from 'flow/components/Setting/Page';
import Loading from 'flow/components/ui-components/Loading';
import RouteLink from 'flow/components/ui-components/RouteLink';
import useAppContext from 'flow/hooks/useAppContext';
import useExtension from 'flow/hooks/useExtension';
import { PbxParams } from 'flow/types/Params';
import Queue from 'flow/types/Queue';
import { routes } from 'flow/types/routes';
import { Sound } from 'flow/types/Sound';

import { TypedMap } from 'flow/types/TypedMap';
import { Voicemail } from 'flow/types/Voicemail';
import { SOUND_PLAYER_ANUMBER } from 'flow/utils';
import { isFlowFreeCustomer } from 'flow/utils/flow-utils';
import { getWelcomeSounds } from 'flow/utils/pbx-utils';
import { List, Map } from 'immutable';
import React, { useEffect } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { generatePath, useHistory, useParams } from 'react-router-dom';

const PbxWelcomeMessageClosed = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { entityKey, profileKey } = useParams<PbxParams>();

    useEffect(() => {
        //time out to wait for server to update after chosing new sound
        setTimeout(() => dispatch(fetchQueue(entityKey)), 1500);
    }, [entityKey]);

    useEffect(() => {
        dispatch(fetchDefaultSounds());
    }, []);

    const { ownExtension } = useExtension();

    const pbxDetails: TypedMap<Queue> = useAppSelector((state) =>
        state.pbx.getIn(['queues', entityKey], Map()),
    );
    const sounds = useAppSelector((state) => state.sounds);

    const profiles = pbxDetails.get('profiles');

    const userSounds = sounds.get('sounds', List());
    const defaultSounds = sounds.get('defaultSounds', Map());
    const queueSounds: TypedMap<Sound> = getWelcomeSounds(
        pbxDetails,
        userSounds,
        defaultSounds,
    );

    if (!pbxDetails || !profiles) {
        return <Loading />;
    }

    /**
     * Play a sound and hangup is represented with the soundDTO:s recording pin attached to
     * a numberDTO presented by {SOUND_PLAYER_ANUMBER}
     *
     * @param redirectNumber
     * @returns the PIN or null
     */
    const getSoundRecordingPINFromNumberDTO = (redirectNumber: string) => {
        return redirectNumber.split(SOUND_PLAYER_ANUMBER)[1];
    };

    const getNumberIfNotSoundRedirect = (redirectNumber: string) => {
        if (
            redirectNumber &&
            !getSoundRecordingPINFromNumberDTO(redirectNumber)
        ) {
            return redirectNumber;
        }
        return null;
    };

    const getVoicemailNameFromRedirect = (redirectNumber: string) => {
        if (redirectNumber && !voicemails.isEmpty()) {
            const voicemail = voicemails
                .filter(
                    (voicemail) =>
                        voicemail.getIn(['number', 'number']) ===
                        redirectNumber,
                )
                .first();

            return voicemail?.get('description');
        }
        return null;
    };

    const getSoundNameFromNumberDTO = (redirectNumber: string) => {
        if (redirectNumber && pbxDetails && !queueSounds.isEmpty()) {
            if (getSoundRecordingPINFromNumberDTO(redirectNumber)) {
                const recordingPIN =
                    getSoundRecordingPINFromNumberDTO(redirectNumber);
                const sound = queueSounds.find(
                    (sound) => sound.get('recordingPIN') === recordingPIN,
                );
                return sound ? sound.get('name') : i18next.t('empty.sound');
            }
        }
        return i18next.t('empty.sound');
    };

    const voicemails = pbxDetails.get('voicemails', Map()) as TypedMap<
        Voicemail[]
    >;
    const profile = profiles.find(
        (profile) => profile.get('key') === profileKey,
    );

    const callForwarding = profile.get('callForwarding');
    const redirectNumber = callForwarding.has('redirect')
        ? callForwarding.getIn(['redirect', 'number'])
        : null;

    const numberToRedirect = getNumberIfNotSoundRedirect(redirectNumber);
    const welcomeSound = getSoundNameFromNumberDTO(redirectNumber);
    const voicemail = getVoicemailNameFromRedirect(redirectNumber);

    const voicemailOption =
        voicemail && isFlowFreeCustomer(ownExtension) ? (
            <SettingsListItem editable={false} description={voicemail}>
                {i18next.t('pbx.name')}
            </SettingsListItem>
        ) : null;

    return (
        <Page
            header="pbx.closed"
            onClickBack={() =>
                history.push(
                    generatePath(
                        routes['pbx.queues.entityKey.settings.welcomemessage'],
                        {
                            entityKey,
                        },
                    ),
                )
            }
        >
            <div className="gap-4">
                <div className="text-xs text-gray-500">
                    {i18next.t(
                        'Queue.WelcomeMessage.Unavailable.TableHeaderTitle',
                    )}
                </div>

                <RouteLink
                    to={generatePath(
                        routes[
                            'pbx.queues.entityKey.settings.welcomemessage.profileKey.redirect'
                        ],
                        { entityKey, profileKey },
                    )}
                    description={
                        numberToRedirect && !voicemail ? numberToRedirect : null
                    }
                >
                    {i18next.t('Queue.WelcomeMessage.Redirect')}
                </RouteLink>

                <RouteLink
                    to={generatePath(
                        routes[
                            'pbx.queues.entityKey.settings.welcomemessage.profileKey.sound'
                        ],
                        { entityKey, profileKey },
                    )}
                    description={!numberToRedirect ? welcomeSound : null}
                >
                    {i18next.t('Queue.WelcomeMessage.PlaySoundAndHangUp')}
                </RouteLink>

                {voicemailOption}
            </div>
        </Page>
    );
};

export default PbxWelcomeMessageClosed;
